import type { CloudSnapshotSummary } from "@board-preserve/contracts";

type UploadedRecoveryPoint = {
  capturedAt: string | null;
  id: string;
};

interface RecoveryPointPickerProps {
  onSelect: (snapshotId: string) => void;
  selectedSnapshotId: string | null;
  snapshots: CloudSnapshotSummary[];
  uploaded?: UploadedRecoveryPoint | null;
}

function formatRecoveryPoint(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatBytes(bytes: number): string {
  if (bytes < 1_000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${(bytes / 1_000).toFixed(1)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

export function RecoveryPointPicker({
  onSelect,
  selectedSnapshotId,
  snapshots,
  uploaded = null,
}: RecoveryPointPickerProps) {
  const sorted = [...snapshots].sort(
    (left, right) =>
      new Date(right.capturedAt).getTime() -
      new Date(left.capturedAt).getTime(),
  );
  const selected = sorted.find(({ id }) => id === selectedSnapshotId);
  const uploadedSelected = uploaded?.id === selectedSnapshotId;

  return (
    <section className="recovery-point-picker" aria-label="Recovery point">
      <div className="recovery-point-heading">
        <div>
          <strong>Recovery point</strong>
          <span>Choose the version of this board to recover from.</span>
        </div>
        {uploadedSelected ? (
          <span className="recovery-source-chip">Uploaded ZIP</span>
        ) : selected ? (
          <span className="recovery-source-chip">
            {selected.capturePurpose === "restore_safety"
              ? "Safety backup"
              : "Cloud backup"}
          </span>
        ) : null}
      </div>

      {sorted.length > 0 || uploaded ? (
        <label className="recovery-point-field">
          <span>Backup</span>
          <select
            onChange={(event) => onSelect(event.currentTarget.value)}
            value={selectedSnapshotId ?? ""}
          >
            {!selectedSnapshotId ? (
              <option value="" disabled>
                Choose a backup
              </option>
            ) : null}
            {uploaded ? (
              <option value={uploaded.id}>
                Uploaded ZIP
                {uploaded.capturedAt
                  ? ` — ${formatRecoveryPoint(uploaded.capturedAt)}`
                  : ""}
              </option>
            ) : null}
            {sorted.map((snapshot) => (
              <option key={snapshot.id} value={snapshot.id}>
                {formatRecoveryPoint(snapshot.capturedAt)} —{" "}
                {formatBytes(snapshot.totalBytes)}
                {snapshot.warningCount > 0
                  ? ` — ${snapshot.warningCount} warning${snapshot.warningCount === 1 ? "" : "s"}`
                  : ""}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <div className="recovery-point-empty">
          <strong>No cloud backups are available</strong>
          <span>Create a backup first, or return and restore from a ZIP.</span>
        </div>
      )}

      {selected ? (
        <dl className="recovery-point-facts">
          <div>
            <dt>Captured</dt>
            <dd>{formatRecoveryPoint(selected.capturedAt)}</dd>
          </div>
          <div>
            <dt>Files copied</dt>
            <dd>{selected.attachmentOutcomes.copied}</dd>
          </div>
          <div>
            <dt>Expires</dt>
            <dd>{formatRecoveryPoint(selected.expiresAt)}</dd>
          </div>
        </dl>
      ) : null}
    </section>
  );
}
